// utils/checkOutcome.js
//
// NEW RULE (what you asked):
// If PEP/Sanctions has matches AND all matches are resolved (i.e. every ref has ≥ 1 reason),
// then:
//   - if ANY reason contains "my client"  -> overall should be REFER (for PEP)
//   - otherwise                           -> treat PEP as CONTINUE (so can become APPROVED overall)
//
// If PEP/Sanctions has matches but NOT all resolved -> force REFER (block approval until confirmed)

import { isVeriffSession } from "../isVeriffSession";
import experianServiceNames from "../Experian/getExperianServiceNames";
import { MY_CLIENT_REASON } from "../getConstants";
import {
  hasOnlySipSanctions,
  isSipOnlySanction,
} from "../Experian/pepSanCards";
// ---------- small helpers ----------
const norm = (v) => (v ?? "").toString().trim().toLowerCase();
const toAliases = (v) => (Array.isArray(v) ? v : [v]).filter(Boolean);

// Safely unwrap Firestore wrapper shapes:
const unwrapExperian = (maybeWrapperOrInner) =>
  maybeWrapperOrInner?.response ?? maybeWrapperOrInner ?? null;

function normalizeCrosscoreDecision(raw) {
  const d = String(raw ?? "")
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!d) return null;
  if (/\bstop\b/.test(d)) return "stop";
  if (/\brefer\b/.test(d)) return "referred";
  if (/\bcontinue\b/.test(d)) return "continue";
  return null;
}

function getCrosscoreDecisionForService(respInner, serviceNames) {
  const r = unwrapExperian(respInner);
  const decisions = r?.clientResponsePayload?.orchestrationDecisions;
  if (!Array.isArray(decisions) || !decisions.length) return null;

  const aliases = toAliases(serviceNames).map((s) => norm(s));
  const hit = decisions.find((d) => aliases.includes(norm(d?.decisionSource)));
  return hit?.decision ?? null;
}

/**
 * Aggregate Experian decisions across your known services
 */
export function getAggregatedExperianDecisionSummary(experianRespInner) {
  const r = unwrapExperian(experianRespInner);
  const decisions = r?.clientResponsePayload?.orchestrationDecisions;

  if (!Array.isArray(decisions) || !decisions.length) {
    return {
      bucket: "awaiting_response",
      hasRefer: false,
      hasStop: false,
      allContinue: false,
      perService: [],
    };
  }

  const servicesToCheck = [
    { key: "ID_VERIFICATION", names: experianServiceNames.ID_VERIFICATION },
    { key: "CREDIT_REFERENCE", names: experianServiceNames.CREDIT_REFERENCE },
    { key: "PEP_SANCTIONS", names: experianServiceNames.PEP_SANCTIONS },
  ];

  const perService = servicesToCheck.map(({ key, names }) => {
    const raw = getCrosscoreDecisionForService(r, names);
    const normalized = normalizeCrosscoreDecision(raw);
    return { service: key, decision: normalized || "none" };
  });

  const decisionsOnly = perService
    .map((x) => x.decision)
    .filter((d) => d !== "none");

  const hasStop = decisionsOnly.includes("stop");
  const hasRefer = decisionsOnly.includes("refer") || decisionsOnly.includes("referred");
  const allContinue =
    decisionsOnly.length > 0 && decisionsOnly.every((d) => d === "continue");

  let bucket = "awaiting_response";
  if (hasStop) bucket = "declined";
  else if (hasRefer) bucket = "referred";
  else if (allContinue) bucket = "approved";

  return { bucket, hasRefer, hasStop, allContinue, perService };
}

// ---------- PEP resolution helpers (PURE) ----------

function extractPepResults(experianRespInner) {
  const r = unwrapExperian(experianRespInner);
  const els = r?.clientResponsePayload?.decisionElements || [];

  const aliases = toAliases(experianServiceNames.PEP_SANCTIONS).map((s) =>
    norm(s)
  );
  const el = els.find((d) => aliases.includes(norm(d?.serviceName)));

  const results = el?.otherData?.response?.searchResults?.[0]?.results || [];
  return Array.isArray(results) ? results : [];
}

function buildPepSanCardRefsFromResults(results) {
  const counters = {};
  const cards = [];

  results.forEach((match) => {
    const types = Array.isArray(match?.matchingCriteria)
      ? [
          ...new Set(
            match.matchingCriteria.map((c) =>
              String(c?.searchType || "").toUpperCase()
            )
          ),
        ]
      : [];

    
    types.forEach((t) => {
      if (isSipOnlySanction(match, t)) return;
      counters[t] = (counters[t] || 0) + 1;
      cards.push({ ref: `${t || "UNK"}${counters[t] || 1}` });
    });
  });

  return cards;
}

function reasonsForRef(reasonsByRef, ref) {
  const entry = reasonsByRef?.[ref];
  const arr = Array.isArray(entry)
    ? entry
    : Array.isArray(entry?.reasons)
    ? entry.reasons
    : [];
  return (arr || []).map((x) => String(x || "").trim()).filter(Boolean);
}

// check “is my client” across all reasons (case-insensitive)
function anyReasonIsMyClient(reasonsByRef, refs, myClientReasons = []) {
  // Prefer your constant list (exact matches), but also allow the phrase "my client"
  const list = (myClientReasons || []).map((x) => norm(x));
  const rx = /\bmy\s*client\b/i;

  for (const c of refs || []) {
    const ref = c?.ref;
    if (!ref) continue;

    const reasons = reasonsForRef(reasonsByRef || {}, ref);

    const hit =
      reasons.some((r) => list.includes(norm(r))) ||
      reasons.some((r) => rx.test(String(r || "")));

    if (hit) return true;
  }
  return false;
}

/**
 * Returns pep status incl anyMyClient
 */
export function getPepResolutionStatus({
  experianResponse,
  reasonsByRef,
  getAllMatchesResolved,
  myClientReasons = [], // add this so it's reusable across app
}) {
  if (!getAllMatchesResolved) {
    return { hasMatches: false, resolved: true, anyMyClient: false, refs: [] };
  }

  const results = extractPepResults(experianResponse);
  const refs = buildPepSanCardRefsFromResults(results);
  if (!refs.length) {
    return {
      hasMatches: false,
      resolved: true,
      anyMyClient: false,
      ignoredSipOnly: hasOnlySipSanctions(results),
      refs: [],
    };
  }

  const resolved = getAllMatchesResolved(refs, (ref) =>
    reasonsForRef(reasonsByRef || {}, ref)
  );

  const anyMyClient = resolved
    ? anyReasonIsMyClient(reasonsByRef, refs, myClientReasons)
    : false;

  return { hasMatches: true, resolved, anyMyClient, refs };
}

/**
 * NEW: PEP override maps to STOP if "my client"
 */
export function applyPepResolutionOverride({ perService, pep }) {
  const effectivePerService = (perService || []).map((s) => {
    if (s.service !== "PEP_SANCTIONS") return s;

    if (pep?.ignoredSipOnly) {
      return {
        ...s,
        decision: "continue",
        note: "All sanctions were SIP-only and were ignored",
      };
    }

    // matches exist but NOT confirmed -> declined (blocked)
    if (pep?.hasMatches && !pep?.resolved) {
      return {
        ...s,
        decision: "stop",
        note: "PEP has matches but not all confirmed -> stop",
      };
    }

    // matches exist and confirmed:
    // - any "my client" -> STOP (declined)
    // - else -> CONTINUE
    if (pep?.hasMatches && pep?.resolved) {
      return {
        ...s,
        decision: pep.anyMyClient ? "referred" : "continue",
        note: pep.anyMyClient
          ? "PEP confirmed: 'my client' -> referred"
          : "PEP confirmed: no 'my client' -> continue",
      };
    }

    return s;
  });

  const effectiveDecisions = effectivePerService
    .map((x) => x.decision)
    .filter((d) => d !== "none");

  return { effectivePerService, effectiveDecisions };
}

// ---------- Veriff bucket ----------
export function getVeriffBucketFromCheckRow(rowOrCheck, checkTypeMaybe) {
  const checkType = rowOrCheck?.checkType ?? checkTypeMaybe;
  const hasVeriff = isVeriffSession(checkType);

  if (!hasVeriff) return "no_veriff";

  const status =
    rowOrCheck?.decision?.verification?.status ??
    rowOrCheck?.verification?.status ??
    null;

  const s = norm(status);

  if (!s) return "awaiting_response";
  if (s === "approved") return "approved";
  if (s === "declined") return "declined";
  if (s === "resubmission_requested") return "resubmission";
  if (s === "expired" || s === "abandoned") return "expired_abandoned";

  return "awaiting_response";
}

/**
 * Main reusable function
 */
export function computeOverallOutcome({
  rowOrCheck,
  checkType,
  experianResponse,
  reasonsByRef,
  getAllMatchesResolved,
  log = false,
  logger = console.log,
}) {
  const veriff = getVeriffBucketFromCheckRow(rowOrCheck, checkType);

  const summary = getAggregatedExperianDecisionSummary(experianResponse);

  // pep status (now includes anyMyClient)
  const pep = getPepResolutionStatus({
    experianResponse,
    reasonsByRef,
    getAllMatchesResolved,
    myClientReasons: MY_CLIENT_REASON,
  });

  // pep override (now does the “my client => refer else continue” rule)
  const { effectivePerService, effectiveDecisions } =
    applyPepResolutionOverride({ perService: summary.perService, pep });

  const hasStop = effectiveDecisions.includes("stop");
  const hasRefer = effectiveDecisions.includes("referred");
  const allContinue =
    effectiveDecisions.length > 0 &&
    effectiveDecisions.every((d) => d === "continue");

  let finalExperian = "awaiting_response";
  if (hasStop) finalExperian = "declined";
  else if (hasRefer) finalExperian = "referred";
  else if (allContinue) finalExperian = "approved";

  if (log) {
    logger("[computeOverallOutcome] combo", {
      veriff,
      pep,
      originalPerService: summary.perService,
      effectivePerService,
      effectiveDecisions,
      finalExperian,
    });
  }

  // ---- PEP Action Required override (highest priority) ----
  // If PEP & Sanctions has matches but NOT resolved, overall result is ALWAYS "Action Required"
  // if (pep?.hasMatches && !pep?.resolved) {
  //   return { checkStatus: "Completed", checkResult: "Action Required" };
  // }

  // ---- combine rules with Veriff ----
  if (veriff === "resubmission")
    return { checkStatus: "Resubmission", checkResult: "" };

  if (veriff === "awaiting_response" || finalExperian === "awaiting_response")
    return { checkStatus: "Processing", checkResult: "" };

  if (veriff === "declined")
    return { checkStatus: "Completed", checkResult: "Declined" };

  // Check if declined due to unresolved PEP matches
  const declinedDueToPep = pep?.hasMatches && !pep?.resolved && hasStop;

  if (veriff === "approved") {
    if (finalExperian === "declined") {
      if (declinedDueToPep)
        return { checkStatus: "Completed", checkResult: "Action Required" };
      return { checkStatus: "Completed", checkResult: "Declined" };
    }
    if (finalExperian === "referred")
      return { checkStatus: "Completed", checkResult: "Referred" };
    if (finalExperian === "approved")
      return { checkStatus: "Completed", checkResult: "Approved" };
  }

  if (veriff === "no_veriff" || veriff === "expired_abandoned") {
    if (veriff === "expired_abandoned")
      return {
        checkStatus: "Completed",
        checkResult: veriff === "no_veriff" ? "No Veriff" : "Expired",
      };
    if (finalExperian === "declined") {
      if (declinedDueToPep)
        return { checkStatus: "Completed", checkResult: "Action Required" };
      return { checkStatus: "Completed", checkResult: "Declined" };
    }
    if (finalExperian === "referred")
      return { checkStatus: "Completed", checkResult: "Referred" };
    if (finalExperian === "approved")
      return { checkStatus: "Completed", checkResult: "Approved" };
  }

  return { checkStatus: "Processing", checkResult: "" };
}
